import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";

// Coût de l'IA du widget de support (edge function support-ai). Source :
// RPC get_support_ai_usage (admin uniquement), migration
// 20260929190000_support_ai_isolation. Le coût est une estimation d'après les
// jetons renvoyés par Vertex, pas la facture Google.

interface UsageDay {
  day: string;
  calls: number;
  errors: number;
  blocked: number;
  input_tokens: number;
  output_tokens: number;
  cost_eur: number;
}

interface SupportAiUsage {
  settings: {
    enabled: boolean;
    daily_call_cap: number;
    hourly_cap_per_person: number;
    daily_cost_alert_eur: number;
  } | null;
  days: UsageDay[];
}

export const SupportAiCostPanel: React.FC<{ days: number }> = ({ days }) => {
  const { t, i18n } = useTranslation("admin");
  const [usage, setUsage] = useState<SupportAiUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    supabase
      .rpc("get_support_ai_usage", { p_days: days })
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) setError(rpcError.message);
        else setUsage(data as SupportAiUsage);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const money = (value: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);

  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
  }).format(new Date());
  const todayRow = usage?.days.find((d) => d.day === today);
  const periodCost = (usage?.days ?? []).reduce(
    (s, d) => s + Number(d.cost_eur),
    0,
  );
  const periodCalls = (usage?.days ?? []).reduce((s, d) => s + d.calls, 0);
  const periodBlocked = (usage?.days ?? []).reduce((s, d) => s + d.blocked, 0);
  const settings = usage?.settings;

  return (
    <section
      className="bg-card border border-border rounded-xl p-4 min-w-0"
      data-testid="support-ai-cost"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t("support_bot.ai_cost.title")}
        </h2>
        {settings && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full border ${
              settings.enabled
                ? "border-green-500/40 text-green-500"
                : "border-destructive/50 text-destructive"
            }`}
          >
            {settings.enabled
              ? t("support_bot.ai_cost.enabled")
              : t("support_bot.ai_cost.disabled")}
          </span>
        )}
      </div>

      {error && (
        <div className="text-sm text-destructive">
          {t("support_bot.error", { message: error })}
        </div>
      )}

      {usage && settings && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile
              label={t("support_bot.ai_cost.today")}
              value={money(Number(todayRow?.cost_eur ?? 0))}
              hint={t("support_bot.ai_cost.calls_of_cap", {
                calls: todayRow?.calls ?? 0,
                cap: settings.daily_call_cap,
              })}
            />
            <Tile
              label={t("support_bot.ai_cost.period", { count: days })}
              value={money(periodCost)}
              hint={t("support_bot.ai_cost.calls", { count: periodCalls })}
            />
            <Tile
              label={t("support_bot.ai_cost.blocked")}
              value={String(periodBlocked)}
              hint={t("support_bot.ai_cost.blocked_hint")}
            />
            <Tile
              label={t("support_bot.ai_cost.alert_threshold")}
              value={money(Number(settings.daily_cost_alert_eur))}
              hint={t("support_bot.ai_cost.per_person", {
                count: settings.hourly_cap_per_person,
              })}
            />
          </div>

          {usage.days.length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left font-normal py-1">
                      {t("support_bot.ai_cost.col_day")}
                    </th>
                    <th className="text-right font-normal py-1">
                      {t("support_bot.ai_cost.col_calls")}
                    </th>
                    <th className="text-right font-normal py-1">
                      {t("support_bot.ai_cost.col_blocked")}
                    </th>
                    <th className="text-right font-normal py-1">
                      {t("support_bot.ai_cost.col_cost")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {usage.days.map((d) => (
                    <tr key={d.day} className="border-t border-border">
                      <td className="py-1 whitespace-nowrap">{d.day}</td>
                      <td className="py-1 text-right">{d.calls}</td>
                      <td className="py-1 text-right">{d.blocked}</td>
                      <td className="py-1 text-right whitespace-nowrap">
                        {money(Number(d.cost_eur))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-3">
            {t("support_bot.ai_cost.note")}
          </p>
        </>
      )}
    </section>
  );
};

const Tile: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="bg-background border border-border rounded-lg p-3 min-w-0">
    <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">
      {label}
    </div>
    <div className="text-xl font-bold text-foreground mt-1 truncate">
      {value}
    </div>
    {hint && (
      <div className="text-xs text-muted-foreground mt-1 truncate">{hint}</div>
    )}
  </div>
);
